import React, { useState, useRef, Fragment } from 'react'
import PropTypes from 'prop-types'
import PlayPauseButton from '@/shared/Buttons/PlayPauseButton'
import DefaultButton from '@/shared/Buttons/Default'
import { useGlobalState } from '@/state'
import PublicDemoRow from '@/pages/Users/PublicProfile/PublicDemoRow'
import useQuery from '@/hooks/useQuery'
import { achievement } from '@/shared/Toast'
import Loading from '@/shared/Loading'
import UserHoverCard from '@/shared/UserHoverCard'
import { TippyTooltip } from '@/shared/ToolTip'
import {
  ChatAltIcon,
  EyeIcon,
  EyeOffIcon,
  ThumbDownIcon,
  StarIcon,
  ArrowCircleUpIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  FilmIcon,
  XCircleIcon,
  LinkIcon
} from '@heroicons/react/solid'

import CommentForm from '@/shared/CommentForm'
import CommentRow from '@/shared/CommentRow'
import SubmissionRowOptions from '@/pages/Users/PublicProfile/PublicProfileSubmissionRowOptions'
import SubmissionShare from '@/pages/Users/PublicProfile/PublicProfileSubmissionRowShare'
import AssignToRoleModal from '@/pages/ManageProjects/AssignToRoleModal'
import { VideoPlayer } from '@/pages/Users/PublicProfile/VideoPlayer'
import GiftModal from '@/shared/GiftModal'
import { colorClasses } from '@/pages/ManageProjects/BucketsManager'
import ScamWarning from '@/shared/ScamWarning'

const SubmissionRow = ({ submission, voteIds, buckets = [], onBucketChange, showIntroOrRun }) => {
  const [currentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const [upvotes, setUpvotes] = useState(submission.cachedVotesUp)
  const [comments, setComments] = useState([])
  const [deleted, setDeleted] = useState(false)
  const [loadingComments, setLoadingComments] = useState(false)
  const [showComments, setShowComments] = useState(false)
  const [reviewed, setReviewed] = useState(false)
  const [upvoted, setUpvoted] = useState(voteIds.indexOf(submission.id) >= 0)
  const [ownerNote, setOwnerNote] = useState(submission.ownerNote || '')
  const [savedOwnerNote, setSavedOwnerNote] = useState(submission.ownerNote || '')
  const [savingOwnerNote, setSavingOwnerNote] = useState(false)
  const [bucketId, setBucketId] = useState(submission.bucketId || '')
  const [confirmingReview, setConfirmingReview] = useState(null)
  const confirmTimeoutRef = useRef(null)
  const { putpostRequest, getRequest } = useQuery()
  const currentBucket = buckets.find((b) => b.id === parseInt(bucketId, 10))

  // Two-click confirmation for destructive owner actions. First click on
  // dismiss/shortlist arms the action and flips the button label to
  // "Confirm <action>?"; second click within 4s actually fires. Auto-
  // resets so users don't return later and accidentally confirm a
  // pre-armed button.
  const tryReview = (action) => {
    if (confirmingReview === action) {
      if (confirmTimeoutRef.current) { clearTimeout(confirmTimeoutRef.current) }
      setConfirmingReview(null)
      review(action)
      return
    }
    if (confirmTimeoutRef.current) { clearTimeout(confirmTimeoutRef.current) }
    setConfirmingReview(action)
    confirmTimeoutRef.current = setTimeout(() => setConfirmingReview(null), 4000)
  }

  const loadComments = () => {
    setShowComments(!showComments)
    setLoadingComments(true)
    getRequest(`/api/v3/auditions/${submission.id}/comments`, {}, (err, jsonData) => {
      setLoadingComments(false)
      if (err) { /* hook */ }
      setComments(jsonData.comments)
    })
  }

  const addChildtoParent = (childComment) => {
    setComments(prev => [childComment, ...prev])
  }

  const upvote = () => {
    if (upvoted) { return }

    if (currentUser.id !== submission.userId) {
      setUpvotes(upvotes + 1)
      setUpvoted(true)
    }

    putpostRequest(`/api/v3/auditions/${submission.id}/upvote`, 'POST', {}, (err, jsonData) => {
      if (err) { /* hook */ return }
      if (jsonData.achievement) {
        setToast(achievement(jsonData.achievement))
      }
      if (!jsonData.achievement && currentUser.id === submission.userId) {
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-red-100 text-red-600">Hey now</p>
          <p className="mt-1 text-sm dark:text-red-300 text-red-400">Silly goose. You can't upvote yourself.</p>
        </div>)
      }
    })
  }
  const incPlayedCount = () => {
    putpostRequest(`/api/v3/auditions/${submission.id}/played`, 'POST', {}, (err, jsonData) => {
      if (err) { /* hook */ }
    })
  }

  const saveOwnerNote = () => {
    if (ownerNote === savedOwnerNote) { return }
    setSavingOwnerNote(true)
    putpostRequest(`/api/v3/auditions/${submission.id}/update_owner_note`, 'POST', { owner_note: ownerNote }, (err, jsonData) => {
      setSavingOwnerNote(false)
      if (err) { return }
      setSavedOwnerNote(jsonData.ownerNote || '')
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Private note saved.</p>
      </div>)
    })
  }

  const performBucketChange = (newBucketId) => {
    const previousBucketId = bucketId
    setBucketId(newBucketId)
    putpostRequest(`/api/v3/auditions/${submission.id}/update_bucket`, 'POST', { bucket_id: newBucketId || null }, (err, jsonData) => {
      // Roll the dropdown back so it stops claiming a bucket the server
      // rejected. Reachable now that any teammate can delete a bucket
      // mid-review, not just the owner.
      if (err) { setBucketId(previousBucketId); return }
      const newId = jsonData.bucketId || ''
      setBucketId(newId)
      if (onBucketChange) { onBucketChange(jsonData.bucketId) }
    })
  }

  const changeBucket = (newBucketId) => {
    if (showIntroOrRun) {
      showIntroOrRun(() => performBucketChange(newBucketId))
    } else {
      performBucketChange(newBucketId)
    }
  }

  const review = (action) => {
    putpostRequest(`/api/v3/auditions/${submission.id}/change_review_status`, 'POST', { review_status: action }, (err, jsonData) => {
      if (err) { /* hook */ }
      setReviewed(true)
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Status changed.</p>
        <p className="mt-1 text-sm dark:text-gray-100 text-gray-500">Marked as {action}.</p>
      </div>)
    })
  }

  if (reviewed) { return null }
  if (deleted) { return null }

  return <li className={`relative bg-white dark:bg-gray-700 px-2 py-2 sm:px-3 rounded-lg shadow-sm${submission.nameGradient ? ' row-with-gradient-name' : ''}${submission.nameGlow ? ' row-with-glow-name' : ''}`}>
    {submission.userCoverImageUrl && (
      <div className='absolute inset-0 overflow-hidden rounded-lg pointer-events-none'>
        <div
          aria-hidden='true'
          className='absolute inset-0 bg-cover bg-center opacity-10 dark:opacity-20 blur-sm'
          style={{ backgroundImage: `url(${submission.userCoverImageUrl})` }}
        />
      </div>
    )}
    <div className="relative flex items-start justify-between flex-col space-y-2">
      <div className='flex space-x-2 items-start justify-between w-full min-w-0'>
        <div className="relative m-1 flex-shrink-0">
          <img className="h-8 w-8 sm:h-16 sm:w-16 rounded-sm bg-gray-200 flex items-center justify-center ring-4 ring-cccblue" src={submission.userImageUrl} alt={submission.displayName} />

          <span className="absolute -bottom-0.5 -right-1 bg-cccblue rounded-tl px-0.5 py-px">
            <FilmIcon className="h-4 w-4 text-white" aria-hidden="true" />
          </span>
        </div>
        <div className="flex-1 py-0 min-w-0 flex flex-col text-left">
          <span className="flex flex-wrap items-center gap-y-1">
            <time className='uppercase text-xs dark:text-gray-300 text-gray-500 mr-1 flex items-center' dateTime={submission.createdAt}>{submission.createdAt}</time>
            <TippyTooltip content={<>How many times this submission has been listened to or clicked on. Only available to premium members.</>}>
              <span type="button" className="ml-2 flex space-x-1 text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 items-center">
                <EyeIcon className="h-4 w-4" aria-hidden="true" />
                <span className="text-xs">{ currentUser.canViewAuditionPlayedCount && <>{submission.playedCount}</> }</span>
              </span>
            </TippyTooltip>
            <TippyTooltip content={<>If this submission was reviewed by project owner. A green check means they have reviewed it. Only available to premium members.</>}>
              <span type="button" className="ml-2 flex space-x-1 text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 items-center">
                { !currentUser.canViewAuditionPlayedCount && <QuestionMarkCircleIcon className="h-4 w-4" aria-hidden="true" /> }
                { currentUser.canViewAuditionPlayedCount && submission.listenedByOwner && <CheckCircleIcon className="text-green-400 h-4 w-4" aria-hidden="true" /> }
                { currentUser.canViewAuditionPlayedCount && !submission.listenedByOwner && <XCircleIcon className="h-4 w-4" aria-hidden="true" /> }
              </span>
            </TippyTooltip>

            <TippyTooltip content={<>Chosen means that the submission was awarded the role. Dismissed means that the project owner was looking for something else at this time.</>}>
              <>
                {submission.status === 'casted' && <span className='ml-2 flex-shrink-0 inline-block px-2 text-white bg-green-500 text-xs font-medium rounded-full flex capitalize'>
                  Chosen
                </span> }
                { currentUser.showRejectedSubmissions && submission.status === 'reject' && <span className='ml-2 flex-shrink-0 inline-block px-2 text-white bg-red-500 text-xs font-medium rounded-full flex capitalize'>
                  Dismissed
                </span> }
              </>
            </TippyTooltip>
            { submission.reviewStatus === 'shortlisted' && (submission.roleStatus === 'closed' || submission.projectDeadlinePassed) && <TippyTooltip content={<>This submission was shortlisted for this role.</>}>
              <span className='ml-2 flex-shrink-0 inline-block px-2 text-white bg-indigo-500 text-xs font-medium rounded-full flex'>SL</span>
            </TippyTooltip> }

            { submission.gifts.map((gift, index) => (
              <TippyTooltip key={`gift${gift.name}${submission.id}`} content={<>Award: {gift.name}</>}>
                <a className='flex' href="/coins">
                  <img className='h-4 w-4 ml-1' alt={gift.name} src={gift.imageUrl} /> { gift.count > 1 && <span className='text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 text-xs'>x {gift.count}</span> }
                </a>
              </TippyTooltip>
            ))}
          </span>
          <div><UserHoverCard username={submission.username} displayName={submission.displayName} gradient={submission.nameGradient} glow={submission.nameGlow} /></div>
          <div className='flex items-center flex-wrap gap-2'>
            <div className="font-bold text-gray-900 dark:text-gray-100">{submission.roleName}</div>
            {/* Surface private submissions so the project owner knows
                at a glance — these aren't visible on the audition's
                profile page. Matches the EyeOffIcon convention used
                by ProfileOptions / ProfileProjects elsewhere. */}
            { submission.permissions === 'private' && (
              <TippyTooltip content={<>This submission is private — only you (as the project owner) and the submitter can see it.</>}>
                <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'>
                  <EyeOffIcon className='h-3 w-3' aria-hidden='true' />
                  Private
                </span>
              </TippyTooltip>
            )}
          </div>
          { submission.remoteLink && <div className='flex w-full min-w-0'>
            <a onClick={incPlayedCount} href={submission.remoteLink} aria-label='Go to submission' target='_blank' rel="noreferrer" className="inline-flex items-start py-2 shadow-sm text-sm font-medium text-cccblue hover:text-cccblue-alt break-all min-w-0">
              <LinkIcon className="mr-1 h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span className='break-all'>{submission.remoteLink}</span>
            </a>
          </div> }
        </div>
        <div className="flex-shrink-0 text-gray-500 dark:text-gray-100 mb-2">
          { submission.audioUrl && <PlayPauseButton
            mediaUrl={submission.audioUrl}
            objectId={submission.id}
            objectKind='Audition' />
          }
        </div>
      </div>
      { submission.videoUrl && currentUser.enabledVideos && <VideoPlayer videoUrl={submission.videoUrl} thumbnailUrl={submission.videomancyThumbnailUrl} /> }
      <ScamWarning text={submission.note} />
      {submission.note && <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300" dangerouslySetInnerHTML={{ __html: submission.note }} /> }
      { submission.demo?.id && <PublicDemoRow demo={submission.demo} /> }
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 w-full">
        <TippyTooltip content={<>Your upvote is only visible to you and the owner of the submission.</>}>
          <button type="button" onClick={upvote} aria-label='Upvote' className="flex space-x-1 text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 items-center">
            <ArrowCircleUpIcon className={`h-5 w-5 ${upvoted ? 'text-green-500' : ''}`} aria-hidden="true" />
            { !upvoted && <>
              { currentUser.id === submission.userId && <span className="text-xs">{upvotes} <span className='hidden sm:inline'>upvotes</span></span> }
              { currentUser.id !== submission.userId && <span className="text-xs hidden sm:inline">upvote</span> }
            </> }
            { Boolean(upvoted) && <span className="text-xs text-green-500 hidden sm:inline">upvoted!</span> }
          </button>
        </TippyTooltip>
        <button onClick={loadComments} aria-label='Load Comments' type="button" className="flex space-x-1 text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 items-center">
          <ChatAltIcon className="h-5 w-5" aria-hidden="true" />
          <span className="text-xs">{submission.commentsCount} <span className='hidden sm:inline'>comments</span> </span>
        </button>
        <SubmissionShare submission={submission} />
        <GiftModal giftableId={submission.id} giftableType='Audition' />
        <SubmissionRowOptions submission={submission} setDeleted={setDeleted} />
      </div>
      <div className="w-full bg-cccblue bg-opacity-20 p-2 rounded-md">
        <div className='flex flex-col text-gray-900 dark:text-gray-100 text-left mb-2'>
          <b>Project Owner Actions:</b>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          { submission.reviewStatus !== 'dismissed' && <DefaultButton
            onClick={() => tryReview('dismissed')}
            className='flex-grow sm:flex-grow-0 justify-center'
            text={
              <div className='flex space-x-1 items-center text-red-500'>
                <ThumbDownIcon className='h-4 w-4' />
                <span>{confirmingReview === 'dismissed' ? 'Confirm dismiss?' : 'Dismiss'}</span>
              </div>
            } /> }
          { submission.reviewStatus !== 'shortlisted' && <DefaultButton
            onClick={() => tryReview('shortlisted')}
            className='flex-grow sm:flex-grow-0 justify-center'
            text={
              <div className='flex space-x-1 items-center text-indigo-500'>
                <StarIcon className='h-4 w-4' />
                <span>{confirmingReview === 'shortlisted' ? 'Confirm shortlist?' : 'Shortlist'}</span>
              </div>
            } /> }
          { submission.reviewStatus !== 'assigned' && <AssignToRoleModal roleId={submission.roleId} submissionId={submission.id} userId={submission.userId} review={() => review('assigned')} /> }
        </div>
        <div className='flex flex-wrap items-center gap-2 mt-3'>
          <span className='text-sm font-medium text-gray-900 dark:text-gray-100'>Bucket:</span>
          { currentBucket && (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses(currentBucket.color).bg} ${colorClasses(currentBucket.color).text}`}>
              {currentBucket.name}
            </span>
          ) }
          { !currentBucket && <span className='text-xs italic text-gray-500 dark:text-gray-400'>none</span> }
          <select
            aria-label='Assign to bucket'
            value={bucketId}
            onChange={(e) => changeBucket(e.target.value)}
            className='text-sm py-1 px-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 ml-auto sm:ml-0'
          >
            <option value=''>— No bucket —</option>
            {buckets.map((b) => (
              <option key={`bktopt${submission.id}${b.id}`} value={b.id}>{b.name}</option>
            ))}
          </select>
          { buckets.length === 0 && <span className='text-xs text-gray-500 dark:text-gray-400'>(create buckets above to categorize)</span> }
        </div>
      </div>
      <div className="w-full bg-cccblue bg-opacity-20 p-2 rounded-md">
        <label htmlFor={`owner-note-${submission.id}`} className='flex flex-col text-gray-900 dark:text-gray-100 mb-1'>
          <b>Private Note:</b>
          <span className='text-xs'>Only visible to you and your team. The submitter will never see it.</span>
        </label>
        <textarea
          id={`owner-note-${submission.id}`}
          value={ownerNote}
          onChange={(e) => setOwnerNote(e.target.value)}
          onBlur={saveOwnerNote}
          rows={2}
          placeholder='Add a private note about this submission...'
          className='w-full text-sm p-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
        />
        <div className='flex justify-start sm:justify-end items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 mt-1'>
          { savingOwnerNote && <span>Saving…</span> }
          { !savingOwnerNote && ownerNote !== savedOwnerNote && <span>Unsaved changes</span> }
          { !savingOwnerNote && ownerNote === savedOwnerNote && savedOwnerNote.length > 0 && <span>Saved</span> }
        </div>
      </div>
      <div className="w-full">
        { showComments && <>
          <CommentForm commentableId={submission.id} commentableType='Audition' addChildtoParent={addChildtoParent} setHide={setShowComments} />
          {comments.length > 0 && <>
            <div className="space-y-3 mt-3">
              {comments.map((com, idx) => (
                <CommentRow key={`com${com.id}`} initialComment={com} />
              ))}
            </div>
          </> }
        </> }
      </div>
      {loadingComments && <Loading /> }
    </div>
  </li>
}

export default SubmissionRow

SubmissionRow.propTypes = {
  submission: PropTypes.object.isRequired,
  voteIds: PropTypes.array.isRequired,
  buckets: PropTypes.array,
  onBucketChange: PropTypes.func,
  showIntroOrRun: PropTypes.func
}
